import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, API, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  ArrowLeft, Download, Printer, CheckCircle2, Clock, CalendarRange, Users,
  MapPin, KeyRound, Banknote, ScanLine, Loader2, Wallet,
} from "lucide-react";

const eur = (v) => `${(Number(v) || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fdate = (s) => {
  if (!s) return "—";
  try {
    return new Date(`${String(s).slice(0, 10)}T12:00:00`)
      .toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch { return s; }
};

const paymentBadge = (b) => {
  if (b.payment_status === "paid") return { label: "Payée en ligne", tone: "ok" };
  if (b.payment_status === "paid_onsite") return { label: "Payée sur place", tone: "ok" };
  if (b.paypal_link) return { label: "À régler via PayPal", tone: "warn" };
  if (b.payment_mode === "online" || b.payment_mode === "paypal") {
    return { label: "Paiement en cours de confirmation", tone: "warn" };
  }
  return { label: "À régler à l'arrivée", tone: "info" };
};

export default function PublicBooking() {
  const { code, token } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const justPaid = params.get("paid") === "1";
  const [data, setData] = useState(null);
  const polls = useRef(0);
  const captured = useRef(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/public/bookings/${token}`);
      setData(d);
      return d;
    } catch (e) {
      setData({ error: formatApiError(e.response?.data?.detail) });
      return null;
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Phase 55 — relance panier : régénère un lien de paiement frais puis redirige
  const [resuming, setResuming] = useState(false);
  const resumePayment = useCallback(async () => {
    setResuming(true);
    try {
      const { data: r } = await api.post(`/public/bookings/${token}/resume-payment`, { origin_url: window.location.origin });
      if (r.checkout_url) window.location.href = r.checkout_url;
      else toast.error("Lien de paiement indisponible");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      load();
    } finally { setResuming(false); }
  }, [token, load]);
  const autoResumed = useRef(false);
  useEffect(() => {
    if (params.get("resume") === "1" && data?.resumable && !autoResumed.current) { autoResumed.current = true; resumePayment(); }
  }, [data, params, resumePayment]);

  // Retour de PayPal : on capture la commande sur le compte de l'établissement.
  useEffect(() => {
    if (params.get("paypal") !== "1" || !data?.booking || captured.current) return;
    if (data.booking.payment_status === "paid") return;
    captured.current = true;
    (async () => {
      try {
        await api.post(`/public/bookings/${token}/paypal/capture`, {
          order_id: params.get("token") || data.booking.paypal_order_id || "",
        });
        toast.success("Paiement PayPal confirmé");
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      }
      load();
    })();
  }, [data, params, token, load]);

  // Le paiement est confirmé par le webhook Stripe : on rafraîchit quelques fois au retour.
  useEffect(() => {
    if (!justPaid || !data?.booking || data.booking.payment_status === "paid") return;
    if (polls.current >= 8) return;
    const t = setTimeout(async () => { polls.current += 1; await load(); }, 2500);
    return () => clearTimeout(t);
  }, [justPaid, data, load]);

  const downloadQr = async () => {
    try {
      const r = await fetch(`${API}/public/bookings/${token}/qr.png`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `QR-${data?.booking?.reference || "reservation"}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("QR code enregistré sur votre appareil");
    } catch { toast.error("Téléchargement impossible — faites une capture d'écran"); }
  };

  if (data?.error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center p-6" data-testid="pb-not-found">
        <div className="max-w-md text-center">
          <ScanLine className="h-10 w-10 mx-auto text-[#FFD700] mb-3" />
          <div className="font-display font-black text-2xl tracking-tight">Réservation introuvable</div>
          <div className="text-sm text-white/60 mt-2">{data.error}</div>
          <Button variant="outline" className="mt-4 border-white/25 text-white hover:bg-white hover:text-black"
                  onClick={() => navigate(`/sites/${code}`)} data-testid="pb-back-error">
            <ArrowLeft className="h-4 w-4 mr-1" />Retour au site
          </Button>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen bg-[#0A0A0A] text-white/50 p-10" data-testid="pb-loading">Chargement…</div>;

  const b = data.booking;
  const ent = data.entreprise || {};
  const labels = data.labels || { unit: "Hébergement" };
  const badge = data.resumable ? { label: "Paiement non finalisé", tone: "warn" } : paymentBadge(b);
  const arrived = b.status === "arrived";
  const cancelled = b.status === "cancelled";
  const balance = b.payment_status === "paid"
    ? Number(b.balance_on_site || 0)
    : (b.payment_status === "paid_onsite" ? 0 : Number(b.total || 0));

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white print:bg-white print:text-black" data-testid="public-booking-page">
      <div className="max-w-3xl mx-auto p-5 lg:p-10">
        <button onClick={() => navigate(`/sites/${code}`)} data-testid="pb-back-link"
                className="text-[11px] uppercase tracking-[0.2em] text-white/50 hover:text-white flex items-center gap-1 mb-4 print:hidden">
          <ArrowLeft className="h-3 w-3" />{ent.name}
        </button>

        {justPaid && b.payment_status !== "paid" && (
          <div className="mb-4 border border-[#F59E0B] bg-[#F59E0B]/10 p-3 text-sm flex items-center gap-2" data-testid="pb-paid-pending">
            <Loader2 className="h-4 w-4 animate-spin text-[#F59E0B]" />
            Paiement reçu — confirmation en cours. Cette page se met à jour automatiquement.
          </div>
        )}
        {cancelled && (
          <div className="mb-4 border border-[#E60000] bg-[#E60000]/10 p-3 text-sm" data-testid="pb-cancelled">
            Cette réservation a été annulée.
          </div>
        )}

        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none">
          {arrived ? "Bienvenue !" : "Votre réservation"}
        </h1>
        <p className="text-base text-white/60 mt-2 print:text-black/60">
          {ent.name} · présentez ce QR code à la réception à votre arrivée.
        </p>

        {/* Billet */}
        <div className="mt-7 border-2 border-[#FFD700] bg-white text-[#0A0A0A] grid sm:grid-cols-[240px_1fr]" data-testid="pb-ticket">
          <div className="p-5 flex flex-col items-center justify-center border-b sm:border-b-0 sm:border-r border-[#DEE2E6]">
            <img src={`${API}/public/bookings/${token}/qr.png`} alt="QR code de la réservation"
                 className="w-[190px] h-[190px] object-contain" data-testid="pb-qr-img" />
            <div className="mt-2 text-[9px] uppercase tracking-[0.2em] font-bold text-[#868E96] flex items-center gap-1">
              <ScanLine className="h-3 w-3" />Scan à la réception
            </div>
          </div>

          <div className="p-5">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96]">
              {labels.unit}
            </div>
            <div className="font-display font-black text-2xl tracking-tight leading-none mt-0.5" data-testid="pb-unit">
              {b.unit_label}
            </div>

            <div className="mt-3 text-sm space-y-1">
              <div className="flex items-center gap-2" data-testid="pb-dates">
                <CalendarRange className="h-4 w-4 text-[#002FA7] shrink-0" />
                <span>Du <b>{fdate(b.date_from)}</b> au <b>{fdate(b.date_to)}</b></span>
              </div>
              <div className="flex items-center gap-2 text-[#495057]" data-testid="pb-guests">
                <Users className="h-4 w-4 shrink-0" />{b.nights} nuit{b.nights > 1 ? "s" : ""} · {b.guests} personne{b.guests > 1 ? "s" : ""}
              </div>
              <div className="flex items-center gap-2 text-[#495057]" data-testid="pb-checkin-times">
                <Clock className="h-4 w-4 shrink-0" />
                Arrivée dès {data.checkin_time} · départ avant {data.checkout_time}
              </div>
              {ent.address && (
                <div className="flex items-center gap-2 text-[#495057]">
                  <MapPin className="h-4 w-4 shrink-0" />{ent.address}
                </div>
              )}
            </div>

            <div className="mt-4 border-t border-[#DEE2E6] pt-3">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#868E96] flex items-center gap-1">
                <KeyRound className="h-3 w-3" />Code de confirmation
              </div>
              <div className="font-mono font-black text-2xl tracking-[0.18em] mt-0.5" data-testid="pb-reference">
                {b.reference}
              </div>
              <div className="text-[11px] text-[#495057] mt-1" data-testid="pb-fallback-note">
                Ce QR code est reconnu automatiquement par le système PAG de l'établissement.
                Si le scan ne fonctionne pas, dictez simplement ce code à la réception : il suffit
                pour retrouver votre réservation.
              </div>
            </div>
          </div>
        </div>

        {/* Paiement */}
        <div className="mt-5 border border-white/15 bg-white/5 p-4 print:border-[#DEE2E6]" data-testid="pb-payment">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 ${
              badge.tone === "ok" ? "bg-[#059669] text-white"
                : badge.tone === "warn" ? "bg-[#F59E0B] text-black" : "bg-[#002FA7] text-white"}`}
                  data-testid="pb-status">
              {badge.label}
            </span>
            {arrived && (
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-1 bg-[#FFD700] text-black"
                    data-testid="pb-arrived">
                <CheckCircle2 className="h-3 w-3 inline mr-1" />Arrivée enregistrée
              </span>
            )}
          </div>
          <div className="mt-3 divide-y divide-white/10 text-sm print:divide-[#DEE2E6]">
            <div className="flex justify-between py-1.5">
              <span className="text-white/60 print:text-black/60">Total du séjour</span>
              <b data-testid="pb-total">{eur(b.total)}</b>
            </div>
            {Number(b.tourist_tax_total || 0) > 0 && (
              <div className="flex justify-between py-1.5">
                <span className="text-white/60 print:text-black/60">dont taxe de séjour</span>
                <span>{eur(b.tourist_tax_total)}</span>
              </div>
            )}
            {(b.extras || []).map((x) => (
              <div key={x.id} className="flex justify-between py-1.5" data-testid={`pb-extra-${x.id}`}>
                <span className="text-white/60 print:text-black/60">
                  {x.label}{x.qty > 1 ? ` × ${x.qty}` : ""}
                </span>
                <span>{eur(x.amount)}</span>
              </div>
            ))}
            {b.payment_status === "paid" && (
              <div className="flex justify-between py-1.5">
                <span className="text-white/60 print:text-black/60">Déjà payé en ligne</span>
                <b className="text-[#059669]">{eur(b.amount_paid || b.amount_due_now)}</b>
              </div>
            )}
            <div className="flex justify-between py-1.5">
              <span className="text-white/60 print:text-black/60 flex items-center gap-1">
                <Banknote className="h-4 w-4" />Reste à régler sur place
              </span>
              <b data-testid="pb-balance">{eur(balance)}</b>
            </div>
          </div>
        </div>

        {data.resumable && (
          <div className="mt-5 border border-[#FFD700] bg-[#FFD700]/10 p-4 print:hidden" data-testid="pb-resume-block">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FFD700]">Votre réservation n'est pas finalisée</div>
            <div className="text-sm text-white/70 mt-1">L'emplacement est retenu à votre nom, mais le paiement de <b className="text-white">{eur(b.amount_due_now || b.total)}</b> n'a pas été confirmé. Finalisez-le maintenant pour recevoir votre confirmation.</div>
            <Button onClick={resumePayment} disabled={resuming} data-testid="pb-resume-payment"
                    className="mt-3 h-12 px-6 bg-[#FFD700] hover:bg-[#E6C200] text-black font-bold">
              <Wallet className="h-4 w-4 mr-2" />{resuming ? "Préparation du paiement…" : `Finaliser mon paiement (${b.payment_mode === "paypal" ? "PayPal" : "carte bancaire"})`}
            </Button>
          </div>
        )}

        <div className="mt-5 flex flex-wrap gap-2 print:hidden">
          {b.paypal_link && balance > 0 && b.payment_status !== "paid" && (
            <a href={b.paypal_link} target="_blank" rel="noopener noreferrer" data-testid="pb-paypal-link">
              <Button className="h-12 px-6 bg-[#003087] hover:bg-[#001F5C] text-white font-bold">
                <Wallet className="h-4 w-4 mr-2" />Payer {eur(b.amount_due_now || b.total)} avec PayPal
              </Button>
            </a>
          )}
          <Button onClick={downloadQr} data-testid="pb-download-qr"
                  className="h-12 px-6 bg-[#FFD700] hover:bg-[#E6C200] text-black font-bold">
            <Download className="h-4 w-4 mr-2" />Ajouter à mon téléphone
          </Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="pb-print"
                  className="h-12 border-white/25 text-white hover:bg-white hover:text-black">
            <Printer className="h-4 w-4 mr-2" />Imprimer
          </Button>
        </div>
        {b.paypal_link && b.payment_status !== "paid" && (
          <div className="mt-2 text-[11px] text-white/50" data-testid="pb-paypal-note">
            Après votre paiement PayPal, la réception valide l'encaissement à votre arrivée.
            Votre QR code est déjà valable comme preuve de réservation.
          </div>
        )}

        <div className="mt-6 text-[11px] text-white/40 print:hidden">
          Conservez cette page en favori : elle reste accessible et se met à jour
          (paiement, arrivée). Un email de confirmation vous a également été envoyé.
        </div>
      </div>
    </div>
  );
}
